import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetchNoEncode } from '../Utils/httpClient';
import { reportSearchError } from '../Utils/reportSearchError';
import { useAppDispatch } from '../Store/types';
import type { paths } from '../Types/api';

type CustomerSearchResponse = paths['/api/customer']['get']['responses'][200]['content']['application/json'];
export type CustomerSearchHit = NonNullable<CustomerSearchResponse['results']>[number];

export const useCustomerSearch = () => {
    const dispatch = useAppDispatch();
    const [results, setResults] = useState<CustomerSearchHit[]>([]);
    const abortControllerRef = useRef<AbortController | null>(null);

    const search = useCallback(
        async (query: string): Promise<void> => {
            abortControllerRef.current?.abort();
            abortControllerRef.current = null;
            setResults([]);

            if (!query.trim()) {
                return;
            }

            const controller = new AbortController();
            abortControllerRef.current = controller;

            try {
                const data = await apiFetchNoEncode<CustomerSearchResponse>(`/api/customer?searchPhrase=${encodeURIComponent(query)}`, { signal: controller.signal });
                if (!controller.signal.aborted) {
                    setResults(data?.results ?? []);
                }
            } catch (error) {
                if (!controller.signal.aborted) {
                    await reportSearchError(dispatch, error);
                }
            }
        },
        [dispatch]
    );

    useEffect(() => () => abortControllerRef.current?.abort(), []);

    return { search, results };
};
